/**
 * MapBackgroundContext — V14.68.6 "Vision Map Partout" (Feb 2026)
 *
 * Provider global qui pilote la carte Leaflet fixée en fond de toutes les
 * pages authentifiées. La carte est figée (non-interactive) mais elle
 * suit dynamiquement le projet / adresse actif via `setLocation()`.
 *
 * Choix UX confirmés par l'utilisateur :
 *   Q1 : overlay bg-slate-950/70 backdrop-blur (équilibre édition/map)
 *   Q2 : appliqué partout (dashboards, settings, éditeurs)
 *   Q3 : tiles satellite Esri World Imagery
 *   Q4 : figé mais suit le projet actif
 *
 * API :
 *   const { center, zoom, visible, setLocation, setVisible, toggle } = useMapBackground();
 *   setLocation([lat, lng], zoom?)   — recentre le fond map
 *   setVisible(true|false)           — cache/affiche le fond map
 *   toggle()                         — bascule visibilité
 *
 * Helper hook :
 *   useSyncMapWithLocation(coords, zoom?) — recentre auto quand coords changent
 */
import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const DEFAULT_CENTER = [46.6, 2.5];   // Centre de la France (vue par défaut)
const DEFAULT_ZOOM = 6;

const VISIBLE_KEY = 'map_bg_visible';

const MapBackgroundContext = createContext(null);

export function MapBackgroundProvider({ children }) {
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [visible, setVisibleState] = useState(false);
  // Certaines pages (HubAddressesPage) ont leur propre map plein écran :
  // on masque le fond pour éviter le double render + les problèmes de perf.
  const [suppressed, setSuppressed] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(VISIBLE_KEY);
      if (v === '1') setVisibleState(true);
    } catch (_e) { /* noop */ }
  }, []);

  const setVisible = useCallback((v) => {
    setVisibleState(v);
    try { localStorage.setItem(VISIBLE_KEY, v ? '1' : '0'); } catch (_e) { /* noop */ }
  }, []);

  const toggle = useCallback(() => setVisible(!visible), [visible, setVisible]);

  const setLocation = useCallback((coords, newZoom) => {
    if (Array.isArray(coords) && coords.length === 2
        && Number.isFinite(coords[0]) && Number.isFinite(coords[1])) {
      setCenter([coords[0], coords[1]]);
      if (Number.isFinite(newZoom)) setZoom(newZoom);
    }
  }, []);

  const resetLocation = useCallback(() => {
    setCenter(DEFAULT_CENTER);
    setZoom(DEFAULT_ZOOM);
  }, []);

  const value = {
    center,
    zoom,
    visible,
    suppressed,
    setLocation,
    resetLocation,
    setVisible,
    toggle,
    setSuppressed,
  };

  return (
    <MapBackgroundContext.Provider value={value}>
      {children}
    </MapBackgroundContext.Provider>
  );
}

export function useMapBackground() {
  const ctx = useContext(MapBackgroundContext);
  if (!ctx) {
    // Fallback silencieux : permet aux composants de vivre hors provider
    // (par ex. LandingPage) sans crash.
    return {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      visible: false,
      suppressed: true,
      setLocation: () => {},
      resetLocation: () => {},
      setVisible: () => {},
      toggle: () => {},
      setSuppressed: () => {},
    };
  }
  return ctx;
}

/**
 * useSyncMapWithLocation — Hook helper : recentre auto le fond map quand
 * `coords` change. À utiliser dans les pages projet/adresse.
 *
 * Ex:
 *   useSyncMapWithLocation(project?.coords, 17);
 */
export function useSyncMapWithLocation(coords, zoom = 17) {
  const { setLocation } = useMapBackground();
  useEffect(() => {
    if (Array.isArray(coords) && coords.length === 2) {
      setLocation(coords, zoom);
    }
  }, [coords?.[0], coords?.[1], zoom, setLocation]);
}

/**
 * useSuppressMapBackground — Hook pour les pages qui ont leur propre map
 * plein écran (ex: HubAddressesPage). Masque le fond global pendant la
 * durée de vie du composant.
 */
export function useSuppressMapBackground() {
  const { setSuppressed } = useMapBackground();
  useEffect(() => {
    setSuppressed(true);
    return () => setSuppressed(false);
  }, [setSuppressed]);
}
